import { useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, ShoppingCart, Package, Tags, Receipt, BarChart3, Users, Settings, LogOut, Menu, Printer, ScanBarcode, Clock, Radio, DatabaseBackup, Contact, BadgePercent, Crown,
} from "lucide-react";
import { NotificationBell } from "./NotificationBell";
import { SyncStatus } from "./SyncStatus";
import { ChangePasswordButton } from "./ChangePassword";
import { BRAND } from "../lib/brand";
import { useAuth } from "../context/AuthContext";
import { useHardware } from "../context/HardwareContext";
import { Sheet, SheetContent, SheetTitle } from "./ui/sheet";
import { Button } from "./ui/button";
import { ThemeToggle } from "./ThemeToggle";
import { InstallButton } from "./InstallButton";
import { CloudSyncBadge } from "./CloudSyncBadge";
import { IS_DESKTOP } from "../lib/desktop";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, perm: ["reports"] },
  { to: "/live", label: "Pantau Live", icon: Radio, perm: ["reports"] },
  { to: "/kasir", label: "Kasir", icon: ShoppingCart, perm: ["pos"] },
  { to: "/transaksi", label: "Transaksi", icon: Receipt },
  { to: "/shift", label: "Shift & Tutup Kasir", icon: Clock },
  { to: "/produk", label: "Produk & Stok", icon: Package, perm: ["products", "labels"] },
  { to: "/kategori", label: "Kategori", icon: Tags, perm: ["categories"] },
  { to: "/pelanggan", label: "Pelanggan & Member", icon: Contact, perm: ["customers", "pos"] },
  { to: "/promo", label: "Promo & Diskon", icon: BadgePercent, perm: ["promos"] },
  { to: "/laporan", label: "Laporan", icon: BarChart3, perm: ["reports"] },
  { to: "/pengguna", label: "Pengguna & Role", icon: Users, perm: ["users"] },
  { to: "/backup", label: "Backup Data", icon: DatabaseBackup, perm: ["backup"] },
  { to: "/pengaturan", label: "Pengaturan", icon: Settings },
  { to: "/super-admin", label: "Super Admin", icon: Crown, superadmin: true },
];

const NavList = ({ onNav }) => {
  const { can, user } = useAuth();
  return (
    <nav className="flex flex-col gap-1">
      {NAV.filter((n) => (n.superadmin ? user?.is_superadmin : !n.perm || n.perm.some(can))).map((n) => (
        <NavLink
          key={n.to}
          to={n.to}
          onClick={onNav}
          data-testid={`nav-${n.to.slice(1)}`}
          className={({ isActive }) =>
            `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
              isActive ? "bg-primary text-primary-foreground shadow-md shadow-primary/20" : "text-muted-foreground hover:bg-accent hover:text-foreground"
            }`
          }
        >
          <n.icon className="h-[18px] w-[18px]" />
          {n.label}
        </NavLink>
      ))}
    </nav>
  );
};

const Brand = () => {
  const { store } = useAuth();
  return (
    <div className="px-2 space-y-3">
      <img src={BRAND.logoFull} alt={BRAND.name} className="h-14 w-auto" data-testid="brand-logo" />
      <div className="flex items-center gap-2 rounded-xl border bg-muted/40 p-2">
        {store?.logo ? <img src={store.logo} alt="" className="h-8 w-8 rounded-lg object-contain bg-white" /> : <div className="h-8 w-8 rounded-lg bg-primary/15 text-primary grid place-items-center font-bold text-sm">{store?.store_name?.[0]}</div>}
        <div className="min-w-0">
          <div className="text-sm font-semibold truncate" data-testid="brand-store-name">{store?.store_name || "..."}</div>
          {store?.tagline && <div className="text-[11px] text-muted-foreground truncate">{store.tagline}</div>}
        </div>
      </div>
    </div>
  );
};

const UserBox = () => {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  return (
    <div className="flex items-center gap-3 p-3 rounded-xl bg-muted/60">
      <div className="h-9 w-9 rounded-full bg-primary/15 text-primary grid place-items-center font-bold">{user?.name?.[0]}</div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold truncate" data-testid="current-user-name">{user?.name}</div>
        <div className="text-xs text-muted-foreground">{user?.role_name}</div>
      </div>
      <Button size="icon" variant="ghost" data-testid="logout-btn" title="Keluar" onClick={async () => { if (await logout()) nav("/login"); }}>
        <LogOut className="h-4 w-4" />
      </Button>
    </div>
  );
};

const HwDot = ({ on, icon: Icon, label }) => (
  <div className="hidden sm:flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-full border" title={label}>
    <Icon className="h-3.5 w-3.5" />
    <span className={`h-2 w-2 rounded-full ${on ? "bg-success" : "bg-muted-foreground/40"}`} />
  </div>
);

export default function AppLayout() {
  const [open, setOpen] = useState(false);
  const { state } = useHardware();
  const { can } = useAuth();
  const loc = useLocation();
  const title = NAV.find((n) => loc.pathname.startsWith(n.to))?.label || "";
  return (
    <div className="min-h-screen flex">
      <aside className="hidden lg:flex w-64 shrink-0 flex-col gap-6 border-r bg-card p-4 sticky top-0 h-screen">
        <Brand />
        <div className="flex-1 overflow-y-auto thin-scroll"><NavList /></div>
        <div className="space-y-1"><UserBox /><ChangePasswordButton /></div>
      </aside>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="left" className="w-72 flex flex-col gap-6 p-4">
          <SheetTitle className="sr-only">Menu</SheetTitle>
          <Brand />
          <div className="flex-1 overflow-y-auto"><NavList onNav={() => setOpen(false)} /></div>
          <div className="space-y-1"><UserBox /><ChangePasswordButton /></div>
        </SheetContent>
      </Sheet>
      <div className="flex-1 min-w-0 flex flex-col">
        <header className="h-16 shrink-0 sticky top-0 z-30 flex items-center gap-3 px-4 lg:px-6 border-b bg-background/80 backdrop-blur-xl">
          <Button size="icon" variant="ghost" className="lg:hidden" data-testid="mobile-menu-btn" onClick={() => setOpen(true)}>
            <Menu className="h-5 w-5" />
          </Button>
          <img src={BRAND.logoMark} alt={BRAND.name} className="h-7 w-auto hidden sm:block lg:hidden" />
          <h1 className="font-display text-base sm:text-lg font-bold tracking-tight flex-1 min-w-0 truncate" data-testid="page-title"><span className="sm:hidden">{title.split(" & ")[0]}</span><span className="hidden sm:inline">{title}</span></h1>
          <span className="hidden sm:contents">
            <HwDot on={!!state.printer} icon={Printer} label={state.printer ? `Printer: ${state.printer.name}` : "Printer belum terhubung"} />
            <HwDot on={!!state.scanner} icon={ScanBarcode} label="Scanner serial" />
          </span>
          {IS_DESKTOP ? <CloudSyncBadge /> : <><SyncStatus /><InstallButton /></>}
          {can("reports") && <NotificationBell />}
          <ThemeToggle />
        </header>
        <main className="flex-1 min-w-0">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
